// Story 7.1 — sign-in form rendered inside the LandingPage AuthCard.
//
// Two fields (email + password). Native useState state machine per the
// project convention (react-hook-form is a dep but has no other
// callers in apps/web today; Story 5.5 BuyListingConfirmModal is the
// canonical state-machine pattern we mirror here).
//
// After 200 success: invalidate the ["account-me"] cache so the SPA
// picks up the newly-established session, then navigate to /marketplace
// (Live-A destination; Story 7.4 moves it to /feed).
//
// 2FA rework (2026-08-17): requires_2fa_step = TRUE means the user has
// TOTP enrolled and holds a partial session — navigate to /2fa/verify to
// collect the code. Login never blocks on ENROLLMENT anymore (that lives
// in /account/preferences; admins are gated at the admin surface instead).

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label } from "@greenhold/components";

import { useSignIn, type SignInError } from "./useSignIn";

export function SignInForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signIn = useSignIn();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<SignInError | null>(null);

  const disabled = signIn.isPending;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      const outcome = await signIn.mutateAsync({ email, password });
      await queryClient.invalidateQueries({ queryKey: ["account-me"] });

      if (outcome.requires_2fa_step) {
        navigate("/2fa/verify", { replace: true });
        return;
      }

      navigate("/feed", { replace: true });
    } catch (err) {
      const e = err as SignInError;
      setError(e);
      setPassword("");
      if (e.code === "EMAIL_NOT_VERIFIED") {
        navigate("/verify-email-pending", { replace: true });
      }
    }
  };

  const errorMessage = mapErrorMessage(error);

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      data-testid="sign-in-form"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="sign-in-email" className="text-brand-paper-100">
          Email
        </Label>
        <Input
          id="sign-in-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          disabled={disabled}
          className="text-brand-forest-900"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="sign-in-password" className="text-brand-paper-100">
          Password
        </Label>
        <Input
          id="sign-in-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          minLength={12}
          required
          disabled={disabled}
          aria-describedby={errorMessage ? "sign-in-error" : undefined}
          className="text-brand-forest-900"
        />
        {errorMessage ? (
          <p
            id="sign-in-error"
            role="alert"
            className="text-sm text-state-error-accent"
          >
            {errorMessage}
          </p>
        ) : null}
      </div>
      <Button
        type="submit"
        variant="primary"
        disabled={disabled}
        aria-busy={disabled}
        className="w-full hover:brightness-110"
        data-testid="sign-in-submit"
      >
        {/* Design tokens don't currently expose an antique-gold hover
            state on primary buttons; brightness-110 is the shadcn
            fallback. Story 7.1b — extend button variants with an
            explicit gold-hover primary. */}
        {disabled ? "Signing in…" : "Sign in"}
      </Button>
      {/* Story 1.12 password-reset backend ships but SPA
          PasswordResetPage does not exist yet. Story 7.1b — add
          `/password-reset` SPA surface + reactivate the link. */}
    </form>
  );
}

function mapErrorMessage(error: SignInError | null): string | null {
  if (!error) return null;
  switch (error.code) {
    case "INVALID_CREDENTIALS":
      return "Email or password not recognised.";
    case "EMAIL_NOT_VERIFIED":
      return "Verify your email before signing in. Redirecting…";
    case "RATE_LIMITED":
      return "Too many attempts. Try again in a few minutes.";
    case "VALIDATION_FAILED":
      return error.message || "Please check your details and try again.";
    default:
      if (error.status >= 500) {
        return "Something's not right on our end. Try again in a moment.";
      }
      return error.message || "Sign-in failed. Try again.";
  }
}
